import { FunctionUtilization } from '@hud/portal-dtos/webapp-function.dto';
import { Service } from '@hud/portal-dtos/webapp-service.dto';
import { Container } from '@hud/web-components/Container/Container';
import DataColumns from '@hud/web-components/DataColumns/DataColumns';
import RunningStatus from '@hud/web-components/RunningStatus/RunningStatus';
import React from 'react';
import ServiceMenu from './ServiceMenu';
import {
  formatDateTime,
  formatPercentage,
} from '@hud/web-components/utils';
import _ from 'lodash';

import { HeaderContainer, ServicesIcon, Title } from './Overview.styles';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';

export interface OverviewProps {
  loading: boolean;
  servicesLoading: boolean;
  services: Service[];
  functions: FunctionUtilization[];
  selectedServiceId: number | null;
  onServiceSelect: (serviceId: number) => void;
}

export default function Overview({
  loading,
  servicesLoading,
  services,
  functions,
  selectedServiceId,
  onServiceSelect,
}: OverviewProps) {
  const service = services.find((service) => service.id === selectedServiceId);

  const codeFiles = _(functions).groupBy('filePath').toPairs().value().length;
  const mappedLines = functions.reduce<number>(
    (acc, func) => acc + func.linesOfCode,
    0,
  );
  const invokedLines = functions.reduce<number>(
    (acc, func) => acc + (func.normalizedRuns > 0 ? func.linesOfCode : 0),
    0,
  );
  const codeUsage = invokedLines / mappedLines;

  return (
    <Container
      title={
        <HeaderContainer>
          <Title>
            <ServicesIcon />
            Service
          </Title>
          {servicesLoading ? (
            <Skeleton height={36} width={100} />
          ) : (
            <ServiceMenu
              services={services}
              selectedServiceId={selectedServiceId}
              onServiceSelect={onServiceSelect}
            />
          )}
        </HeaderContainer>
      }
      variant="solid"
      aria-label="Live Code Overview"
    >
      <DataColumns.Columns>
        <DataColumns.Column>
          <DataColumns.Row title="Status" loading={loading}>
            {service ? <RunningStatus status={service.state} /> : null}
          </DataColumns.Row>

          <DataColumns.Row title="Functions mapped" loading={loading}>
            {service?.totalFunctions}
          </DataColumns.Row>
        </DataColumns.Column>
        <DataColumns.Column>
          <DataColumns.Row title="Last seen" loading={loading}>
            {service?.lastSeenAt
              ? formatDateTime(service.lastSeenAt, true)
              : '-'}
          </DataColumns.Row>

          <DataColumns.Row title="Code files" loading={loading}>
            {codeFiles}
          </DataColumns.Row>
        </DataColumns.Column>
        <DataColumns.Column>
          {/* title:Mapped lines of code */}
          <DataColumns.Row title="Mapped lines of code" loading={loading}>
            {mappedLines}
          </DataColumns.Row>
          {/* title:Invoked lines of code */}
          <DataColumns.Row title="Invoked lines of code" loading={loading}>
            {invokedLines}
          </DataColumns.Row>
          {/* title:Code usage */}
          <DataColumns.Row title="Code usage" loading={loading}>
            {formatPercentage(codeUsage)}
          </DataColumns.Row>
        </DataColumns.Column>
      </DataColumns.Columns>
    </Container>
  );
}
